import type { ReactNode } from "react";

import type { VehicleControl } from "@/api/client";
import { LEVEL_LABEL, levelName } from "@/modules/fleet/catalogue";

/** What `vehicle_control_status` (field 68) says the vehicle is doing.
 *
 *  THE HERO OF THE LIVE WINDOW. The page leads with board IDs and GPS, and the
 *  question an operator actually opens it to answer is whether the bike in
 *  front of them can move.
 *
 *  REDESIGNED FOR A NARROW COLUMN (Dattatray, 2026-10-21). The previous
 *  version was a hero bar with a sentence under it, which is a wide shape: in
 *  the dashboard's left column the ignition chips wrapped across three ragged
 *  lines and reflowed whenever the window moved. This is a VERTICAL
 *  composition instead, so the panel keeps one shape at every width it is
 *  given: state, then authority, then inputs.
 *
 *  THREE THINGS HE ASKED TO DROP, and they are gone: the "free to move"
 *  sentence, the age of the reading, and the "none holding" caption. Said
 *  plainly because each had a reason and the reason is now overridden -- the
 *  age in particular was D-115's staleness line. The page head carries a LIVE
 *  chip, so the page is not silent about freshness; this panel is.
 *
 *  WHAT SURVIVED, and why. The mockup he approved shows exactly ONE state:
 *  mobilised, fresh, nothing held. It is silent on malformed, unknown, stale,
 *  armed and seat-reported, so those keep the behaviour they were given after
 *  live incidents:
 *    * `armed` says IT CAN STILL MOVE and never says immobilised;
 *    * a vehicle enforcing the WRONG action is restrained, never mobile;
 *    * a malformed word is reported, not interpreted, and is not quietened
 *      by the device having since gone silent;
 *    * no word at all reads as UNKNOWN, never as a reassuring green state;
 *    * a stale reading loses the status colour, and says so in words.
 *
 *  THE AUTHORITY BLOCK IS A 2x4 GRID, not a chip row and not one
 *  "immobilised by X" line. `levels_held` is a SET -- several actors can hold
 *  one vehicle at once -- and the HIGHEST open level decides the type in
 *  force (OFL-06k). A grid gives every actor a fixed position, so reading
 *  "who holds it" is a scan down a column instead of parsing a sentence, and
 *  the level number carries the hierarchy.
 *
 *  NO BIT ARITHMETIC IN THIS FILE. The layout lives in the firmware's C and
 *  in `shared/lib/vehicle_control.py`; the server hands over a decoded block.
 *  A third copy in the browser is how a renumbered bit ends up looking right
 *  on screen while reporting a restraint that moved.
 *
 *  ONE TYPEFACE. Montserrat only, per `tests/ui/test_one_typeface.py` and
 *  Dattatray's "No other font is allowed"; where a value must read as data it
 *  gets tabular figures, not a second face.
 */

/** The eight actor levels, by the name the console shows.
 *
 *  MIRRORS `LEVEL_NAME` in `shared/lib/vehicle_control.py` and exists only so
 *  the grid can draw a row for an actor that is NOT holding. The server names
 *  the ones that are (`levels_named`), and those names win wherever they are
 *  present: this list never decides what a held level is called.
 */
/** THE ASSIGNED LEVELS, AND ONLY THOSE -- renumbered 2026-10-24 (D-131).
 *
 *  THIS FILE IS WHERE THE INVENTED NAMES SHIPPED. It carried its own list of
 *  eight -- Driver 1, Dealer 2, Company 3, Operator 4, Financier 5, OEM 6,
 *  Regulator 7, Platform 8 -- of which five were names nobody had approved and
 *  one was wrong about a real level. Dattatray found it by asking who had
 *  approved the eight levels. The server and the Fleet page were corrected
 *  with the renumber and this was not, so the deployed page still read
 *  "Dealer" afterwards; it was caught by grepping the SERVED bundle rather
 *  than by any test.
 *
 *  FIVE ROWS, NOT EIGHT, and that is the substantive change rather than the
 *  names. Levels 3, 7 and 8 have no actor, and a grid row for one invited
 *  somebody to emit it -- which is the one state the mobilise AND gate can
 *  never clear, because no actor maps to it and nothing can release it. The
 *  old list made four impossible parties look like they could hold a vehicle.
 *
 *  `LEVEL_LABEL` IS IMPORTED RATHER THAN RESTATED. One vocabulary for the
 *  console; the labels carry their number because precedence resolves on it.
 *  Level 2 is listed although Orbit cannot emit it yet -- a device CAN report
 *  it once the theft-report route exists, and a hold that arrives must have a
 *  name. */
const ASSIGNED: readonly number[] = Object.keys(LEVEL_LABEL)
  .map(Number).sort((a, b) => a - b);

/** The rows to draw: every ASSIGNED level, plus any level the device actually
 *  reports holding even though no actor maps to it.
 *
 *  THE SECOND HALF IS NOT TIDINESS. Narrowing the grid to assigned levels
 *  means an unmapped level the device reports would get NO ROW -- so the one
 *  hold state the mobilise AND gate can never clear would be the one state
 *  invisible on this page. The server already names it honestly
 *  (`level_name()` -> "level N (unassigned)"); this makes sure it is drawn. */
function actorRows(held: readonly number[]): ReadonlyArray<readonly [number, string]> {
  const extra = held.filter((l) => !(l in LEVEL_LABEL)).sort((a, b) => a - b);
  //: PLAIN NAMES HERE. This grid prints the level number in its own
  //: column, so a label ending in "(5)" would print it twice -- and the
  //: server's name for a held level has no ordinal, so the two spellings
  //: appeared in one column depending only on whether the level was held.
  return [...ASSIGNED, ...extra].map((n) => [n, levelName(n)] as const);
}

/** Read down, not across: the first half in the left column, the rest in the
 *  right. A CSS grid fills row by row, so the DOM order has to be interleaved
 *  -- and it is COMPUTED now, because it was a hand-written [0,4,1,5,2,6,3,7]
 *  for exactly eight entries and would have silently dropped rows at five. */
/** Read down, not across: the first half in the left column, the rest in the
 *  right. A CSS grid fills row by row, so the DOM order has to be interleaved.
 *  COMPUTED from the count, because it was a hand-written [0,4,1,5,2,6,3,7]
 *  for exactly eight entries and would silently drop rows at any other size --
 *  and the size now varies with what the device reports. */
const GRID_ORDER = (n: number): number[] => {
  const half = Math.ceil(n / 2);
  const out: number[] = [];
  for (let i = 0; i < half; i++) {
    out.push(i);
    if (i + half < n) out.push(i + half);
  }
  return out;
};

type State = "mobile" | "armed" | "held" | "bad" | "unknown";

/** The state, as ONE LINE. Three statuses, and Dattatray's words for them:
 *
 *    Mobilised
 *    Immobilised (freewheel)  /  Immobilised (wheel lock)
 *    Immobilised commanded (waiting for vehicle to stop)
 *
 *  THE BRACKET IS A SEPARATE, SMALLER LINE (`qual`), not part of the status
 *  string. *"Waititing fro vehicle to stop should in small font, same for
 *  freewheel or Throttle lock."* -- and at the hero's weight the longest one
 *  wrapped to two full-width lines, so the qualifier was shouting as loudly
 *  as the status and pushing the card taller than the panels beside it.
 *
 *  SPLIT RATHER THAN STYLED INLINE because the two are different claims: the
 *  word is WHAT STATE the vehicle is in, the qualifier is which kind or what
 *  it is waiting for. Keeping them as one string would mean every reader --
 *  a test, a screen reader, a future filter -- has to parse brackets back out
 *  to get the state.
 *
 *  *"So many issues with this message. We can have main status as Mobilised,
 *  Immbilized, Immobilized Comamnded (). Immobilized Comamnded : in thsi
 *  state we can show : Waiting fro vehciel to Stop. Nothing else is
 *  required."* What was there instead read:
 *
 *    freewheel armed
 *    Commanded but not actuated. IT CAN STILL MOVE.
 *    Commanded freewheel, enforcing none, decided by Platform operator (1)
 *
 *  Three lines, two of them restating the first, one of them saying
 *  "enforcing none" about a vehicle it had just called armed. "freewheel
 *  armed" was also the only status not phrased as a state of the VEHICLE.
 *
 *  THE SAFETY POINT SURVIVES THE REWRITE, which is why I did not argue it.
 *  `armed` must never read as "immobilised": the vehicle has the command, has
 *  NOT actuated, and CAN STILL MOVE -- a distinction that cost this product a
 *  live console lie. "Waiting for vehicle to stop" says exactly that, in
 *  words an operator does not have to decode, and it keeps `armed` a separate
 *  status rather than a shade of the other one.
 *
 *  WHAT WENT, and where it still is:
 *    * the DECIDING level -- in the "Held by" grid, which marks every holding
 *      level, and in each row's hover. I had moved it here one commit
 *      earlier on my own judgement, not at his request.
 *    * "enforcing X, though it was asked for Y" -- an action/command
 *      divergence, which cannot occur while both actions drive the same
 *      output. Kept in the rows, not the headline.
 *
 *  `bad` IS NOT ONE OF THE THREE AND STAYS. A word the device cannot produce
 *  is a FAULT; rendering it as one of three normal statuses would file a
 *  firmware bug under "nothing to see".
 */
function headline(vc: VehicleControl): {
  state: State; word: string; qual?: string; warn?: ReactNode } {
  if (vc.malformed) {
    return {
      // `bad`, not `unknown`: a word the device cannot produce is a FAULT
      // and must look like one. `unknown` is the muted tone, and wearing it
      // would file a firmware fault under "nothing to see".
      state: "bad", word: "Reading looks wrong",
      warn: <>{vc.malformed}. The device should not be able to report this, so it
        is not interpreted; it wants a look at the firmware.</>,
    };
  }
  if (vc.armed) {
    // THE PARENTHETICAL IS THE WHOLE WARNING NOW, not a second line under a
    // first. It has to stay attached to the status, because the status alone
    // ("Immobilised commanded") would read as done.
    return {
      state: "armed", word: "Immobilised commanded",
      qual: "waiting for vehicle to stop",
    };
  }
  if (vc.is_applied || vc.restrained) {
    // THE TYPE IN THE PARENS, as Dattatray asked: *"Immobilized : Show
    // freewheel or throttle lock"*.
    //
    // `applied_name`, NOT `commanded_name` -- WHAT THE VEHICLE IS ENFORCING,
    // not what it was told. The two are equal under `is_applied`; the second
    // branch is the case where they diverge (the vehicle actuated something
    // other than the command), and that branch exists precisely so that
    // reading never falls through to a green "Mobilised". Naming the command
    // there would describe a restraint the vehicle is not applying.
    //
    // HE WROTE "throttle lock" AND I HAVE NOT USED IT. The two actions are
    // `freewheel` (ACT 2: throttle CUT, wheels spin) and `wheel lock`
    // (ACT 3: wheels locked), and "throttle lock" describes the FIRST one --
    // so using it for the second would swap the two in the one place an
    // operator reads them. These are `ACTION_NAME` from
    // shared/lib/vehicle_control.py, which mirrors the firmware's `ACT_*`.
    // Say the word and I will rename both ends; I will not rename one.
    return { state: "held", word: "Immobilised", qual: vc.applied_name };
  }
  return { state: "mobile", word: "Mobilised" };
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="lw-vc-ig">
      <span />
      <span className="lw-vc-ik">{k}</span>
      <span className="lw-vc-st">{v}</span>
    </div>
  );
}

export function VehicleControlCard({ vc, fresh = true }: {
  vc: VehicleControl | null;
  /** Is the DEVICE currently reporting? Passed in rather than computed, so
   *  this panel does not add a FOURTH liveness threshold to a console that
   *  already has three in disagreement (TECH_DEBT 31).
   *
   *  IT STILL CHANGES THE RENDER, even though the age text is gone. A
   *  nine-hour-old reading drawn in full-strength ok-green IS an assertion of
   *  currency: colour and size are read first. The state and its words are
   *  unchanged; the tone goes neutral and one line says why. */
  fresh?: boolean;
}) {
  // NOT THE SAME AS "nothing is held". A device on older firmware never sends
  // field 68, and rendering that as a green "Mobilised" would invent the most
  // reassuring possible answer out of silence.
  if (!vc) {
    return (
      <section className="panel lw-vc unknown">
        <div className="lw-vc-top"><span className="lw-vc-lbl">Vehicle control</span></div>
        <div className="lw-vc-hero">
          <div className="lw-vc-word">Unknown</div>
          <div className="lw-vc-warn">
            This device has never reported <span className="mono">vehicle_control_status</span>.
            That is older firmware, <strong>not</strong> a vehicle with nothing
            held: Orbit cannot see what this one is enforcing.
          </div>
        </div>
      </section>
    );
  }

  const h = headline(vc);
  // A reading nobody can vouch for loses the status colour and keeps
  // everything else. `malformed` is the exception: that is a fault in the
  // DEVICE's own report, true whenever it was sent, and it must not be
  // quietened by the device having gone quiet afterwards.
  const quiet = !fresh && !vc.malformed;
  const held = new Set(vc.levels_held);
  // The rows to draw -- assigned levels, plus any unmapped level the DEVICE
  // reports, so a hold that no actor maps to cannot be invisible here.
  const actors = actorRows(vc.levels_held);

  return (
    <section className={`panel lw-vc ${quiet ? "unknown" : h.state}`}>
      <div className="lw-vc-top"><span className="lw-vc-lbl">Vehicle control</span></div>

      <div className="lw-vc-hero">
        {/* The raw word keeps no visible row -- nobody reads a bitfield off a
            dashboard -- but it stays in the hover, where it is still the
            number to put in a bug report. */}
        <div className="lw-vc-word"
          title={`vehicle_control_status = ${vc.word} (0x${vc.word.toString(16)})`}>
          {h.word}
        </div>
        {/* SMALLER AND UNDER IT, never inline: at the hero's size "(waiting
            for vehicle to stop)" wrapped to a second full-width line and read
            as a second status. Still inside the status block rather than
            down with the warnings -- it qualifies the word above it and is
            meaningless on its own. */}
        {h.qual && <div className="lw-vc-qual">({h.qual})</div>}
        {h.warn && <div className="lw-vc-warn">{h.warn}</div>}
        {/* SAID IN WORDS, not left to the desaturation. The colour is the fast
            signal; this is the one that survives a greyscale print, a
            colourblind reader, and somebody reading the screen out loud. */}
        {quiet && (
          <div className="lw-vc-warn">
            LAST KNOWN: this is the <strong>last reading the device sent</strong>,
            not what the vehicle is doing now.
          </div>
        )}
        {/* THE "Commanded X, enforcing Y, decided by Z" LINE IS GONE.
            *"Nothing else is required."* It restated the status above it, and
            in the `armed` case contradicted it outright -- "Commanded
            freewheel, enforcing none" under a headline saying the vehicle was
            armed, which is three readings of one fact and no clear answer to
            "can this vehicle move".

            I had added the "decided by" half of it one commit earlier on my
            own judgement when the grid's "decides" chip went. It was not
            asked for, and the grid already marks every holding level with the
            deciding one named in its hover. */}
      </div>

      <div className="lw-vc-sec">
        <div className="lw-vc-sk">Held by</div>
        <div className="lw-vc-acts">
          {GRID_ORDER(actors.length).map((i) => {
            const [lvl, fallback] = actors[i];
            const at = vc.levels_held.indexOf(lvl);
            const on = held.has(lvl);
            // the server's name wins where it has one
            const name = at >= 0 ? (vc.levels_named[at] ?? fallback) : fallback;
            const decides = on && lvl === vc.deciding_level;
            //: `undefined` when Orbit has no hold for a level the DEVICE
            //: holds -- a BLE-originated immobilise -- which is why this is
            //: not defaulted to 0.
            return (
              <div key={lvl}
                className={`lw-vc-act${on ? " holds" : ""}`}
                title={[
                  decides
                    ? `Level ${lvl}: the highest open level, so its type is the one in force (OFL-06k)`
                    : on
                      ? `Level ${lvl}: holding, but a higher level decides the type`
                      : `Level ${lvl}: not holding`,
                ].filter(Boolean).join(" ")}>
                {/* TWO COLUMNS: LEVEL AND NAME. The third carried the
                    2-bit reason, then the hold's `cause`, and both are gone
                    (D-133, then 2026-10-26). WHICH LEVEL holds the vehicle is
                    the whole answer -- a level IS a party, so naming it names
                    who is holding and who can release. */}
                <span className="lw-vc-lvl">{lvl}</span>
                <span className="lw-vc-nm">{name}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="lw-vc-sec">
        <div className="lw-vc-sk">Ignition</div>
        <div className="lw-vc-ign">
          {/* Never colour alone: this console's --ok and --warn are 4.1 dE
              apart under protanopia, so the glyph and the WORDS carry it. */}
          <div className={`lw-vc-ig${vc.physical_ignition ? " on" : ""}`}
            title="The physical ignition line (bit 0)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" aria-hidden="true">
              <circle cx="8" cy="8" r="4.5" /><path d="M11 11 L20 20 M17 18 L19 16" />
            </svg>
            <span className="lw-vc-ik">Physical Ignition</span>
            <span className="lw-vc-st">{vc.physical_ignition ? "● ON" : "○ OFF"}</span>
          </div>
          <div className={`lw-vc-ig${vc.ble_ignition ? " on" : ""}`}
            title="Ignition asserted over BLE by the rider's app (bit 1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 7 L17 17 L12 22 V2 L17 7 L7 17" />
            </svg>
            <span className="lw-vc-ik">BLE Ignition</span>
            <span className="lw-vc-st">{vc.ble_ignition ? "● ON" : "○ OFF"}</span>
          </div>
          {/* NOT AN IGNITION SOURCE, and the wording has to keep that clear.
              This bit says a phone is in BLE range; it does not start the
              vehicle, so the state reads where the phone IS. */}
          <div className={`lw-vc-ig${vc.ble_connected ? " on" : ""}`}
            title="A phone is connected over BLE right now (bit 2). Proximity, not ignition.">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18 H13" />
            </svg>
            <span className="lw-vc-ik">Phone</span>
            <span className="lw-vc-st">{vc.ble_connected ? "● Nearby" : "○ Away"}</span>
          </div>
          {/* ABSENT FIELDS ARE NOT CONTENT. "Seat: not reported" held a
              labelled row, for ever, on every vehicle without a seat lock. */}
          {vc.seat && (
            <Row k="Seat" v={vc.seat === "locked" ? "▪ locked" : "▫ unlocked"} />
          )}
        </div>
      </div>
    </section>
  );
}
